// CB:BEGIN silent-ticket-signin-v2
// Silent ticket sign-in (one-shot)
import type { Clerk } from '@clerk/clerk-js';
import { getClerkEnv } from '../config/clerk';
import { getApiUrl } from '../lib/getApiUrl';

let signing = false;

export async function silentTicketSignIn(email: string, profile?: { firstName?: string; lastName?: string; phone?: string }) {
  if (signing) return false;
  signing = true;
  try {
    const env = getClerkEnv();
    const resp = await fetch(getApiUrl('/api/auth/ensure-user-and-token'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, env, ...profile }),
    });
    if (!resp.ok) return false;
    const { token } = await resp.json();
    if (!token) return false;

    const clerk = (window as any).Clerk as Clerk | undefined;
    if (!clerk) return false;
    await clerk.load();
    const si = await clerk.client.signIn.create({ strategy: 'ticket', ticket: token });
    // CB:BEGIN client-trust-ticket – only set session when complete; if needs_second_factor, caller can redirect to /sign-in
    if (si?.status !== 'complete' || !si?.createdSessionId) {
      if (si?.status === 'needs_second_factor') {
        console.warn('Ticket sign-in requires second factor; redirect user to /sign-in to complete.');
      }
      return false;
    }
    await clerk.setActive({ session: si.createdSessionId });
    return true;
  } catch (e) {
    console.error('Ticket redeem failed:', e);
    return false;
  } finally {
    signing = false;
  }
}
// CB:END silent-ticket-signin-v2

// CB:BEGIN legacy-useHeadlessAuth
// Legacy export for backward compatibility
import { useSignIn } from '@clerk/clerk-react';

export function useHeadlessAuth() {
  const { isLoaded } = useSignIn();

  async function silentSignIn(identifier: string, _password?: string, profile?: { firstName?: string; lastName?: string; phone?: string }) {
    if (!isLoaded) return false;
    if (signing) return false;       // prevent parallel use of the same ticket
    signing = true;

    try {
      // Use environment-aware configuration to match backend
      const env = getClerkEnv();
      console.info('[AuthFlow] calling ensure-user-and-token', { env, email: identifier });

      // Ask backend for a ticket for THIS env
      const r = await fetch(getApiUrl('/api/auth/ensure-user-and-token'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: identifier, env, ...profile }),
      });
      
      if (!r.ok) {
        const errorText = await r.text();
        console.warn('ensure-user-and-token failed', errorText);
        return false;
      }
      
      const { token, userId, env: backendEnv } = await r.json();
      if (!token) return false;

      console.log('🎫 Redeeming sign-in token...', { userId, backendEnv });
      
      // Use Frontend API to redeem ticket
      const clerk = (window as any).Clerk as Clerk | undefined;
      if (!clerk) {
        console.error('❌ Clerk not available');
        return false;
      }
      
      await clerk.load();   // ensure client/session initialized
      console.log('✅ Clerk loaded, attempting ticket redemption...');

      // A ticket works only once; redeem immediately
      const si = await clerk.client.signIn.create({ strategy: 'ticket', ticket: token });
      // CB:BEGIN client-trust-silent-signin – only set session when complete; if needs_second_factor, return false
      if (si?.status !== 'complete' || !si?.createdSessionId) {
        if (si?.status === 'needs_second_factor') {
          console.warn('❌ Ticket sign-in requires second factor; user should complete sign-in at /sign-in');
        } else {
          console.error('❌ Failed to create session from ticket');
        }
        return false;
      }

      console.log('✅ Session created, setting active...');
      await clerk.setActive({ session: si.createdSessionId });
      console.log('✅ User signed in successfully!');
      return true;
      // CB:END client-trust-silent-signin
    } catch (e) {
      console.error('❌ Ticket redeem failed:', e);
      return false;
    } finally {
      signing = false;
    }
  }

  return { isLoaded, silentSignIn };
}
// CB:END legacy-useHeadlessAuth
